/**
 * Loader utility that manages the BProgress bar for page navigation
 */
import { BProgress, css } from '@bprogress/core';

/**
 * @knipignore
 */
export interface ILoader {
   start(): void;
   done(): void;
   set(progress: number): void;
   inc(): void;
   initNavEvents(): void;
}

// Navigation utilities
const NavigationUtils = {
   extractPathFromUrl(url: string): string {
      try {
         const urlObj = new URL(url);
         return urlObj.pathname;
      } catch {
         return url;
      }
   },

   isSamePath(urlA: string, urlB: string): boolean {
      return this.extractPathFromUrl(urlA) === this.extractPathFromUrl(urlB);
   },

   isInternalNavigation(link: HTMLAnchorElement): boolean {
      return !!(
         link?.href &&
         !link.target &&
         !link.download &&
         link.hostname === window.location.hostname &&
         !this.isCheckoutNavigation(link)
      );
   },

   isCheckoutNavigation(link: HTMLAnchorElement): boolean {
      const ssoRegex = /^\/User\/sso\/shopify\/([^?]+)\?checkout=1&return_to=/;
      return ssoRegex.test(link.pathname + link.search);
   },

   isStandardLeftClick(event: MouseEvent): boolean {
      return event.button === 0 && !event.ctrlKey && !event.metaKey;
   },
};

// Progress bar styling
const ProgressBarStyles = {
   applyStyles() {
      const styleElement = document.createElement('style');
      styleElement.textContent = css({
         color: 'var(--color-brand-600, oklch(46.46% 0.138 251.76))',
         height: '4px',
      });
      document.head.append(styleElement);
   },
};

// Loader implementation
const Loader = {
   start() {
      BProgress.start();
   },

   done() {
      BProgress.done();
   },

   set(progress: number) {
      BProgress.set(progress);
   },

   inc() {
      BProgress.inc();
   },

   initNavEvents() {
      // Apply styling
      ProgressBarStyles.applyStyles();

      // Configure progress bar
      BProgress.configure({
         minimum: 0.5,
         easing: 'ease',
         speed: 2000,
         trickle: true,
         trickleSpeed: 300,
         showSpinner: false,
      });

      // Path tracking
      let currentPath = NavigationUtils.extractPathFromUrl(window.location.href);

      // Handle initial page load
      this.setupInitialLoadHandler();

      // Handle page unload
      window.addEventListener('beforeunload', event => {
         this.start();
         this.clearIfNavigationCanceled(event);
      });

      // History navigation (back/forward)
      this.setupHistoryNavigationHandler(currentPath, newPath => {
         currentPath = newPath;
      });

      // Link click navigation
      this.setupLinkClickHandler();

      // Set progress bar box-shadow
      document
         .querySelector('.bprogress .bar')
         ?.setAttribute('style', 'box-shadow: 0 0 8px #11161a, 0 0 4px #11161a;');
   },

   /**
    * An unsaved-changes guard can turn the unload into a "Leave site?"
    * dialog. If the user chooses to stay, no unload follows and nothing
    * would ever clear the bar, so the page looks stuck loading forever.
    * Timers pause while the dialog is up, so a zero-delay timeout runs
    * right after it's dismissed: if a guard flagged the event, clear the
    * bar. Guards signal either via preventDefault() or by assigning a
    * message to the legacy returnValue.
    */
   clearIfNavigationCanceled(event: BeforeUnloadEvent) {
      setTimeout(() => {
         const prompted =
            event.defaultPrevented || (typeof event.returnValue === 'string' && event.returnValue);
         if (prompted) {
            this.done();
         }
      }, 0);
   },

   setupInitialLoadHandler() {
      if (document.readyState === 'loading') {
         this.start();
         window.addEventListener('load', () => this.done());
      }
   },

   setupHistoryNavigationHandler(currentPath: string, updatePath: (path: string) => void) {
      window.addEventListener('popstate', () => {
         const newPath = NavigationUtils.extractPathFromUrl(window.location.href);

         if (newPath !== currentPath) {
            this.start();
            updatePath(newPath);
         }
      });
   },

   setupLinkClickHandler() {
      document.addEventListener('click', event => {
         if (!NavigationUtils.isStandardLeftClick(event)) return;

         const link =
            event.target instanceof Element
               ? (event.target.closest('a:not([data-action]):not(.tab-link)') as HTMLAnchorElement)
               : null;
         if (!link || !NavigationUtils.isInternalNavigation(link)) return;

         const linkPath = NavigationUtils.extractPathFromUrl(link.href);
         const currentLocationPath = NavigationUtils.extractPathFromUrl(window.location.href);

         if (linkPath !== currentLocationPath) {
            // Deferred so other handlers finish first; if one of them
            // swallowed the click (an in-app anchor whose component calls
            // preventDefault, like a search-result overlay link), no
            // navigation follows and a started bar would never clear.
            setTimeout(() => {
               if (!event.defaultPrevented) {
                  this.start();
               }
            }, 0);
         }
      });
   },
};

export default Loader as ILoader;
